import { type RecordLayoutAttributes } from './generated/RecordLayoutAttributes';
import ApplicationModel, { attr } from '../ApplicationModel';
import { model } from '../modelBuilder';
import { recordFieldsForRecordType } from 'components/CardLayoutEditor/models/record_fields';
import CardView from './CardView';
import CardRow from './CardRow';
import CardGroup from './CardGroup';
import CardField from './CardField';
import CardStyling from './CardStyling';
import { HasCardLayoutHierarchy } from './concerns/HasCardLayoutHierarchy';
import { JsonQuerySerialization } from './concerns/JsonQuerySerialization';
import type RecordFields from 'components/CardLayoutEditor/models/RecordFields';
import { type CardViewType } from 'javascripts/shared-types';
import { type CardLayoutModelTypeName } from 'components/CardLayoutEditor/models/types';

export const ALL_CARD_VIEWS: CardViewType[] = [
  'expanded',
  'collapsed',
  'whiteboard',
];
export const DEFAULT_CARD_VIEWS: CardViewType[] = ['expanded', 'collapsed'];

interface RecordLayout extends RecordLayoutAttributes, HasCardLayoutHierarchy {
  recordFields: RecordFields;
  recordType: CardLayoutModelTypeName;
}

@model
@HasCardLayoutHierarchy
@JsonQuerySerialization
class RecordLayout extends ApplicationModel {
  static defaultScope(cardViews: CardViewType[] = DEFAULT_CARD_VIEWS) {
    const cardViewScope = CardView.select(
      'coverImages',
      'inherited',
      'showParentRecord'
    ).merge({
      rows: CardRow.select('id', 'divider').merge({
        firstGroup: CardGroup.select('id').merge({
          fields: CardField.select('id', 'type', 'key').merge({
            customFieldDefinition: ['id', 'key', 'name', 'type'],
          }),
        }),
        secondGroup: CardGroup.select('id').merge({
          fields: CardField.select('id', 'type', 'key').merge({
            customFieldDefinition: ['id', 'key', 'name', 'type'],
          }),
        }),
      }),
      cardStyling: CardStyling.select('type'),
    });

    const cardViewScopes = {};
    cardViews.forEach(cardView => (cardViewScopes[cardView] = cardViewScope));

    return RecordLayout.select('recordType').merge(cardViewScopes);
  }

  // Start auto-generated
  static typename: 'RecordLayout' = 'RecordLayout';
  get typename(): 'RecordLayout' {
    return 'RecordLayout';
  }

  static inputType = 'RecordLayoutAttributes';

  static fields = {
    recordType: attr({ readable: true }),
    collapsed: attr.belongsTo({
      writeShape: [
        'rows',
        'coverImages',
        'inherited',
        'showParentRecord',
        'cardStyling',
      ],
      readable: true,
      typename: 'CardView',
    }),
    expanded: attr.belongsTo({
      writeShape: [
        'rows',
        'coverImages',
        'inherited',
        'showParentRecord',
        'cardStyling',
      ],
      readable: true,
      typename: 'CardView',
    }),
    whiteboard: attr.belongsTo({
      writeShape: [
        'rows',
        'coverImages',
        'inherited',
        'showParentRecord',
        'cardStyling',
      ],
      readable: true,
      typename: 'CardView',
    }),
  };
  // End auto-generated

  initialize() {
    const RecordFieldClass = recordFieldsForRecordType(this.recordType);
    this.recordFields = new RecordFieldClass(this);
  }

  get parentRecordIsAvailable() {
    switch (this.recordType) {
      case 'Feature':
        if (!this.cardLayout.epicsEnabled) {
          return false;
        }

        return true;

      case 'Requirement':
        return true;

      default:
        return false;
    }
  }

  get modelClass() {
    return ApplicationModel.models[this.recordType];
  }

  fieldByName(name) {
    return this.recordFields.field(name);
  }
}

export default RecordLayout;
